import DataGrid, { Column, SearchPanel, Scrolling } from 'devextreme-react/data-grid';
import { CardAnalytics } from '../../../components/library/card-analytics/CardAnalytics';
import Button from 'devextreme-react/button';
import { useAppSelector } from '../../../contexts/hooks';
import { EReportId } from '../../../types/types.dashboard';
import { useApiDashBoard } from '../../../api/api.dashboards';
import { useState } from 'react';
import { useScreenSize } from '../../../utils/media-query';
import { useApiOrders } from '../../../api/api.orders';
import { resButtonsMail } from '../../../images/icons';
import { Tooltip } from 'devextreme-react';

export const BoardOrdersPendingForMail = () => {
  const { getData } = useApiDashBoard();
  const { findOrder } = useApiOrders();
  const [dataLoading, setDataLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isXSmall, isSmall } = useScreenSize();

  const dashboard = useAppSelector((x) => x.dashboards.dashboards.find((x) => x.Db_Id === EReportId.OrdersPendingForMail));

  if (!dashboard) {
    return <CardAnalytics title='Órdenes pendientes por correo' contentClass='sales' />;
  }

  const handleRefreshClick = () => {
    getData(EReportId.OrdersPendingForMail);
  };

  const handleViewClick = (data: any) => {
    if (!data.Res_Ord_Id) {
      return;
    }
    setLoading(true);
    findOrder(undefined, data.Res_Ord_Id, setLoading, true);
  };

  // Render personalizado para la primera columna con iconos de mail y adjuntos
  const renderOrderWithIconsCell = (data: any) => {
    const hasAttachments = data.data.TieneAdjuntos === true || data.data.TieneAdjuntos === 1;
    const orderId = `order-${data.data.Res_Ord_Id}`;
    const attachmentId = `attachment-${data.data.Res_Ord_Id}`;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
        {/* Icono de mail */}
        <img
          id={orderId}
          src={resButtonsMail}
          alt='Mail'
          style={{
            width: '16px',
            height: '16px',
            opacity: 0.7,
          }}
        />

        {/* Icono de adjunto solo si tiene adjuntos */}
        {hasAttachments && (
          <span
            id={attachmentId}
            style={{
              fontSize: '12px',
              color: '#ff9800',
              cursor: 'help',
            }}>
            �
          </span>
        )}

        {/* Número de orden */}
        <span
          style={{
            backgroundColor: '#f8f9fa',
            color: '#495057',
            border: '1px solid #dee2e6',
            padding: '3px 8px',
            borderRadius: '8px',
            fontSize: '0.8rem',
            fontWeight: '600',
            fontFamily: 'monospace',
            cursor: 'default',
          }}>
          {data.value || 'N/A'}
        </span>

        {/* Tooltips */}
        <Tooltip target={`#${orderId}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
          Orden pendiente por envío de correo
        </Tooltip>

        {hasAttachments && (
          <Tooltip target={`#${attachmentId}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
            Contiene adjuntos
          </Tooltip>
        )}
      </div>
    );
  };

  // Render personalizado para la columna de paciente (simplificado)
  const renderPatientCell = (data: any) => {
    const patientName = data.value || 'Sin nombre';
    const isLongName = patientName.length > 30;
    const maxLength = isXSmall || isSmall ? 20 : 30;
    const truncatedName = isLongName ? `${patientName.substring(0, maxLength)}...` : patientName;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
        title={isLongName ? patientName : undefined}>
        <span
          style={{
            fontSize: '0.8rem',
            fontWeight: '500',
            color: patientName === 'Sin nombre' ? '#999' : '#495057',
            lineHeight: '1.2',
            cursor: isLongName ? 'help' : 'default',
            fontStyle: patientName === 'Sin nombre' ? 'italic' : 'normal',
          }}>
          {truncatedName}
        </span>
      </div>
    );
  };

  // Render personalizado para el botón de acción
  const renderActionCell = (data: any) => {
    const isSmallScreen = isXSmall || isSmall;
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <Button text={isSmallScreen ? '' : 'Ver'} icon={isSmallScreen ? 'eyeopen' : undefined} type='default' stylingMode='contained' onClick={() => handleViewClick(data.data)} hint='Ver detalles de la orden' height={26} />
      </div>
    );
  };

  return (
    <CardAnalytics title={`${dashboard.Data?.length || 0} Pendientes por envío de correo`} contentClass='sales' onRefreshClick={handleRefreshClick} lastUpdate={dashboard.UltimaActualizacion} isLoading={dashboard.Loading || dataLoading || loading}>
      <DataGrid
        height='100%'
        columnAutoWidth={true}
        allowColumnResizing={false}
        dataSource={dashboard.Data}
        keyExpr='Res_Ord_Id'
        showBorders={false}
        showRowLines={true}
        showColumnLines={false}
        rowAlternationEnabled={true}
        hoverStateEnabled={true}
        onRowPrepared={(e) => {
          if (e.rowType === 'data') {
            e.rowElement.style.transition = 'all 0.2s ease';
            e.rowElement.style.height = '32px';
            e.rowElement.style.minHeight = '32px';
          }
        }}
        onCellPrepared={(e) => {
          if (e.rowType === 'data') {
            e.cellElement.style.padding = '4px 8px';
            e.cellElement.style.fontSize = '13px';
            e.cellElement.style.lineHeight = '1.2';
          }
        }}>
        <SearchPanel visible={true} highlightCaseSensitive={true} placeholder='Buscar órdenes...' />
        <Scrolling mode='virtual' />

        {/* Columna Número de Orden con iconos */}
        <Column dataField='Ord_Numero' caption='ORDEN' cellRender={renderOrderWithIconsCell} allowFiltering={true} width={160} sortOrder='desc' />

        {/* Columna Paciente */}
        <Column dataField='Paciente' caption='PACIENTE' cellRender={renderPatientCell} allowFiltering={true} calculateCellValue={(data) => data.Paciente || 'Sin nombre'} />

        {/* Columna Acciones */}
        <Column caption='ACCIONES' alignment='center' cellRender={renderActionCell} width={90} />
      </DataGrid>
    </CardAnalytics>
  );
};
